/**
 * OBS-facing types and protocol constants (SPEC §8).
 *
 * Pinned to OBS Studio 32.2.2, which bundles obs-websocket 5.7.4 (RPC v1). The
 * vendored protocol lives in `tools/obs-protocol/`; constants here are checked
 * against it by `tools/obs-protocol` tests.
 */
import { z } from 'zod';

/** OBS Studio version this suite is pinned to and tested against. */
export const PINNED_OBS_VERSION = '32.2.2';
/** obs-websocket version bundled with {@link PINNED_OBS_VERSION}. */
export const PINNED_OBS_WEBSOCKET_VERSION = '5.7.4';
/** obs-websocket RPC version. */
export const OBS_RPC_VERSION = 1;

/** obs-websocket `WebSocketOpCode`. */
export const ObsOpCode = {
  Hello: 0,
  Identify: 1,
  Identified: 2,
  Reidentify: 3,
  Event: 5,
  Request: 6,
  RequestResponse: 7,
  RequestBatch: 8,
  RequestBatchResponse: 9,
} as const;

/** obs-websocket `WebSocketCloseCode`. */
export const ObsCloseCode = {
  DontClose: 0,
  UnknownReason: 4000,
  MessageDecodeError: 4002,
  MissingDataField: 4003,
  InvalidDataFieldType: 4004,
  InvalidDataFieldValue: 4005,
  UnknownOpCode: 4006,
  NotIdentified: 4007,
  AlreadyIdentified: 4008,
  AuthenticationFailed: 4009,
  UnsupportedRpcVersion: 4010,
  SessionInvalidated: 4011,
  UnsupportedFeature: 4012,
} as const;

/** obs-websocket `RequestStatus`. */
export const ObsRequestStatus = {
  Unknown: 0,
  NoError: 10,
  Success: 100,
  MissingRequestType: 203,
  UnknownRequestType: 204,
  GenericError: 205,
  UnsupportedRequestBatchExecutionType: 206,
  NotReady: 207,
  MissingRequestField: 300,
  MissingRequestData: 301,
  InvalidRequestField: 400,
  InvalidRequestFieldType: 401,
  RequestFieldOutOfRange: 402,
  RequestFieldEmpty: 403,
  TooManyRequestFields: 404,
  OutputRunning: 500,
  OutputNotRunning: 501,
  OutputPaused: 502,
  OutputNotPaused: 503,
  OutputDisabled: 504,
  StudioModeActive: 505,
  StudioModeNotActive: 506,
  ResourceNotFound: 600,
  ResourceAlreadyExists: 601,
  InvalidResourceType: 602,
  NotEnoughResources: 603,
  InvalidResourceState: 604,
  InvalidInputKind: 605,
  ResourceNotConfigurable: 606,
  InvalidFilterKind: 607,
  ResourceCreationFailed: 700,
  ResourceActionFailed: 701,
  RequestProcessingFailed: 702,
  CannotAct: 703,
} as const;

/** obs-websocket `EventSubscription` bitmask values. */
export const ObsEventSubscription = {
  None: 0,
  General: 1 << 0,
  Config: 1 << 1,
  Scenes: 1 << 2,
  Inputs: 1 << 3,
  Transitions: 1 << 4,
  Filters: 1 << 5,
  Outputs: 1 << 6,
  SceneItems: 1 << 7,
  MediaInputs: 1 << 8,
  Vendors: 1 << 9,
  Ui: 1 << 10,
  Canvases: 1 << 11,
  /** All low-volume categories. Excludes the high-volume ones below. */
  All:
    (1 << 0) | (1 << 1) | (1 << 2) | (1 << 3) | (1 << 4) | (1 << 5) |
    (1 << 6) | (1 << 7) | (1 << 8) | (1 << 9) | (1 << 10) | (1 << 11),
  InputVolumeMeters: 1 << 16,
  InputActiveStateChanged: 1 << 17,
  InputShowStateChanged: 1 << 18,
  SceneItemTransformChanged: 1 << 19,
} as const;

/** obs-websocket `ObsOutputState`, carried by `StreamStateChanged` / `RecordStateChanged`. */
export const ObsOutputState = {
  Unknown: 'OBS_WEBSOCKET_OUTPUT_UNKNOWN',
  Starting: 'OBS_WEBSOCKET_OUTPUT_STARTING',
  Started: 'OBS_WEBSOCKET_OUTPUT_STARTED',
  Stopping: 'OBS_WEBSOCKET_OUTPUT_STOPPING',
  Stopped: 'OBS_WEBSOCKET_OUTPUT_STOPPED',
  Reconnecting: 'OBS_WEBSOCKET_OUTPUT_RECONNECTING',
  Reconnected: 'OBS_WEBSOCKET_OUTPUT_RECONNECTED',
  Paused: 'OBS_WEBSOCKET_OUTPUT_PAUSED',
  Resumed: 'OBS_WEBSOCKET_OUTPUT_RESUMED',
} as const;
export type ObsOutputStateValue = (typeof ObsOutputState)[keyof typeof ObsOutputState];

/** libobs `obs_bounds_type`, as serialized by obs-websocket in `sceneItemTransform.boundsType`. */
export const OBS_BOUNDS_TYPES = [
  'OBS_BOUNDS_NONE',
  'OBS_BOUNDS_STRETCH',
  'OBS_BOUNDS_SCALE_INNER',
  'OBS_BOUNDS_SCALE_OUTER',
  'OBS_BOUNDS_SCALE_TO_WIDTH',
  'OBS_BOUNDS_SCALE_TO_HEIGHT',
  'OBS_BOUNDS_MAX_ONLY',
] as const;
export const ObsBoundsTypeSchema = z.enum(OBS_BOUNDS_TYPES);
export type ObsBoundsType = z.infer<typeof ObsBoundsTypeSchema>;

/** libobs alignment bit flags. `0` is centered on both axes. */
export const ObsAlignment = {
  Center: 0,
  Left: 1 << 0,
  Right: 1 << 1,
  Top: 1 << 2,
  Bottom: 1 << 3,
} as const;

/** Every valid alignment value: center/left/right combined with center/top/bottom. */
export const VALID_OBS_ALIGNMENTS: readonly number[] = [0, 1, 2, 4, 5, 6, 8, 9, 10];
export const ObsAlignmentSchema = z
  .number()
  .int()
  .refine((v) => VALID_OBS_ALIGNMENTS.includes(v), {
    message: `alignment must be one of ${VALID_OBS_ALIGNMENTS.join(', ')} (OBS_ALIGN_* flags)`,
  });

/**
 * A scene item transform as returned by `GetSceneItemList` / `GetSceneItemTransform`.
 * `width`/`height`/`sourceWidth`/`sourceHeight` are read-only in OBS.
 */
export interface ObsSceneItemTransform {
  positionX: number;
  positionY: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  width: number;
  height: number;
  sourceWidth: number;
  sourceHeight: number;
  alignment: number;
  boundsType: ObsBoundsType;
  boundsAlignment: number;
  boundsWidth: number;
  boundsHeight: number;
  cropLeft: number;
  cropRight: number;
  cropTop: number;
  cropBottom: number;
  cropToBounds: boolean;
}

/** The writable subset of {@link ObsSceneItemTransform}, as sent in `SetSceneItemTransform`. */
export type ObsSceneItemTransformInput = Partial<
  Omit<ObsSceneItemTransform, 'width' | 'height' | 'sourceWidth' | 'sourceHeight'>
>;

/**
 * A claimed OBS scene item (SPEC §8.3). Transforms and visibility are addressed by
 * `sceneUuid + sceneItemId`, never by source: one input can appear in several scenes.
 */
export const ObsSceneItemBindingSchema = z.strictObject({
  sceneUuid: z.string().min(1),
  sceneItemId: z.number().int().min(0),
  /** For validation and diagnostics only, never the transform address. */
  sourceUuid: z.string().min(1),
  /** Source name when the binding was claimed. Diagnostics only. */
  sourceNameSnapshot: z.string(),
});
export type ObsSceneItemBinding = z.infer<typeof ObsSceneItemBindingSchema>;

/**
 * One camera slot of a scene (SPEC §6). The three pointers stay independently
 * repointable: a fix-up for one must not disturb the others.
 */
export const CameraSlotSchema = z.strictObject({
  slotId: z.string().min(1),
  /** Which scene item shows video. */
  obsBinding: ObsSceneItemBindingSchema.nullable(),
  /** Who is nameplated. */
  participantId: z.string().min(1).nullable(),
  /** Which CoreVideo feed is routed here. */
  liveFeedId: z.string().min(1).nullable(),
});
export type CameraSlot = z.infer<typeof CameraSlotSchema>;

/**
 * True for requests neither app may ever send (SPEC §2, §13): toggles (always explicit
 * start/stop), creating/removing/reconfiguring inputs, scenes, profiles or collections,
 * OBS configuration, T-bar and transition overrides, hotkeys, vendor requests, and UI
 * dialogs or projectors. Studio mode requests are not here: they are control-app only
 * ({@link STUDIO_MODE_REQUESTS}, ADR 0003).
 *
 * Most of these exist in obs-websocket 5.7.4; they are excluded by policy (SPEC §8.2).
 * See `docs/obs-protocol-verification.md`; checked against the vendored protocol in
 * tools/obs-protocol. The OBS clients refuse these locally before sending.
 */
/**
 * Studio mode writes (Preview → TAKE, ADR 0003): sent by the control app only, and only
 * on the operator's action. The graphics app refuses them like a forbidden request.
 */
export const STUDIO_MODE_REQUESTS: ReadonlySet<string> = new Set(['SetStudioModeEnabled', 'SetCurrentPreviewScene', 'TriggerStudioModeTransition']);

export function isForbiddenRequest(requestType: string): boolean {
  return (
    /^(Toggle|Create|Remove|Duplicate|Open|TriggerHotkey|Press|CallVendorRequest|Broadcast|Sleep)/.test(requestType) ||
    /^Set.*(Settings|Name|Directory|Parameter|Index)$/.test(requestType) && requestType !== 'SetSceneItemIndex' ||
    /^SetInputAudio/.test(requestType) ||
    /^Set(CurrentSceneCollection|CurrentProfile|SceneItemLocked|SceneItemBlendMode|SceneItemPrivateSettings|PersistentData|SourceFilter.*|MediaInput.*|SceneSceneTransitionOverride|TBarPosition|CurrentSceneTransitionSettings|StreamServiceSettings|VideoSettings|RecordDirectory|OutputSettings|ProfileParameter)$/.test(requestType) ||
    /^(TriggerMediaInputAction|SaveReplayBuffer|StartReplayBuffer|StopReplayBuffer|ToggleReplayBuffer|SendStreamCaption|StartOutput|StopOutput|SaveSourceScreenshot|GetSourceScreenshot)$/.test(requestType)
  );
}
